import { useCallback, useMemo } from "react";
import { useNavigate, useLocation } from "react-router";
import type { ComponentType, SVGProps, MouseEvent } from "react";
import type { To } from "react-router";
import ChatRoundDotsLinear from "~icons/solar/chat-round-dots-linear";
import CalendarMarkLinear from "~icons/solar/calendar-mark-linear";
import CaseRoundLinear from "~icons/solar/case-round-linear";
import BuildingsLinear from "~icons/solar/buildings-linear";
import Buildings2Linear from "~icons/solar/buildings-2-linear";
import BookmarkSquareMinimalisticLinear from "~icons/solar/bookmark-square-minimalistic-linear";
import ChatRoundLikeLinear from "~icons/solar/chat-round-like-linear";
import UsersGroupRoundedLinear from "~icons/solar/users-group-rounded-linear";
import UserPlusLinear from "~icons/solar/user-plus-linear";
import ClipboardListLinear from "~icons/solar/clipboard-list-linear";
import MagniferLinear from "~icons/solar/magnifer-linear";
import GraphLinear from "~icons/solar/graph-linear";
import ChartSquareLinear from "~icons/solar/chart-square-linear";
import { useChatStore } from "../../stores/chat-store";
import { useAuthStore } from "../../stores/auth-store";
import { usePermissions } from "../../hooks/usePermissions";
import { env } from "../../lib/env";
import { SEARCH_TRACE_ALLOWED_EMAIL } from "../../lib/search-trace-allowlist";
import { Permissions } from "../../types/permissions";
import type { Permission } from "../../types/permissions";

const FEEDBACK_ENABLED = env.FEATURE_FEEDBACK_ENABLED;
const SEARCH_TRACE_ENABLED = env.SEARCH_TRACE_ENABLED;
const ANALYTICS_ENABLED = env.FEATURE_ANALYTICS_ENABLED;

export interface NavItemDef {
  to: To;
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  label: string;
  end?: boolean;
  /** If set, item is rendered only when the user has this permission. */
  requiredPermission?: Permission;
  onClick?: (e: MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => void;
}

export function useSidebarNav() {
  const navigate = useNavigate();
  const location = useLocation();
  const { hasPermission } = usePermissions();
  const userEmail = useAuthStore((s) => s.user?.email);
  const searchTraceVisible =
    SEARCH_TRACE_ENABLED && userEmail === SEARCH_TRACE_ALLOWED_EMAIL;

  // "Chat with AI" sidebar entry: resume the active conversation if one
  // exists in the store; otherwise land on a fresh /chat. This pairs with
  // the persistent floating panel so messages survive navigation. Use the
  // "New Chat" button in ChatHistoryPanel (or the floating panel header)
  // to explicitly start a new thread.
  const handleNewChat = useCallback(
    (e: MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
      e.preventDefault();
      const { messages, threadId } = useChatStore.getState();
      const target =
        messages.length > 0 ? `/chat?t=${threadId}` : "/chat";
      navigate(target, { replace: location.pathname === "/chat" });
    },
    [navigate, location.pathname]
  );

  const navItems = useMemo<NavItemDef[]>(() => {
    const allItems: NavItemDef[] = [
      {
        to: "/chat",
        icon: ChatRoundDotsLinear,
        label: "Chat with AI",
        onClick: handleNewChat,
        requiredPermission: Permissions.AI_CHAT,
      },
      { to: "/meetings", icon: CalendarMarkLinear, label: "Meetings" },
      {
        to: "/projects",
        icon: CaseRoundLinear,
        label: "Projects",
        requiredPermission: Permissions.PROJECT_VIEW,
      },
      {
        to: "/clients",
        icon: BuildingsLinear,
        label: "Clients",
        requiredPermission: Permissions.CLIENT_VIEW,
      },
      {
        to: "/companies",
        icon: Buildings2Linear,
        label: "Company Directory",
        requiredPermission: Permissions.COMPANY_VIEW,
      },
      {
        to: "/talent-pools",
        icon: BookmarkSquareMinimalisticLinear,
        label: "Talent Pools",
        requiredPermission: Permissions.TALENT_POOL_VIEW,
      },
      {
        to: "/talent",
        icon: MagniferLinear,
        label: "Discover Talent",
        requiredPermission: Permissions.CANDIDATE_SEARCH,
      },
      {
        to: "/candidates/ingest",
        icon: UserPlusLinear,
        label: "Ingest Candidate",
        requiredPermission: Permissions.CANDIDATE_INGEST_MANUAL,
      },
      {
        to: "/candidates/ingestions/mine",
        icon: ClipboardListLinear,
        label: "My Ingestions",
        requiredPermission: Permissions.CANDIDATE_INGEST_MANUAL,
      },
      ...(FEEDBACK_ENABLED
        ? [
            {
              to: "/feedback" as const,
              icon: ChatRoundLikeLinear,
              label: "Feedback",
            },
          ]
        : []),
      ...(ANALYTICS_ENABLED
        ? [
            {
              to: "/analytics" as const,
              icon: ChartSquareLinear,
              label: "Analytics",
              requiredPermission: Permissions.ANALYTICS_VIEW_OWN,
            },
          ]
        : []),
      {
        to: "/admin/users",
        icon: UsersGroupRoundedLinear,
        label: "Users",
        requiredPermission: Permissions.USER_VIEW,
      },
      ...(searchTraceVisible
        ? [
            {
              to: "/admin/search-traces",
              icon: GraphLinear,
              label: "Search Traces",
              requiredPermission: Permissions.SEARCH_TRACE_VIEW,
            },
          ]
        : []),
    ];

    return allItems.filter(
      (item) => !item.requiredPermission || hasPermission(item.requiredPermission)
    );
  }, [handleNewChat, hasPermission, searchTraceVisible]);

  return { navItems, handleNewChat };
}
